import { useActivityTemplateType } from "PFApp/activities/hooks";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { useErrorsGrowl } from "PFCore/hooks";
import { useCreateTag } from "PFCore/hooks/queries/activities/tags/use_create_tag";
import { useDeleteTag } from "PFCore/hooks/queries/activities/tags/use_delete_tag";
import { useUpdateTag } from "PFCore/hooks/queries/activities/tags/use_update_tag";
import { useAuditEngagementsInvalidate } from "PFCore/hooks/queries/audit_planner/audit_engagements/use_audit_engagements_invalidate";
import { useAuditRolesInvalidate } from "PFCore/hooks/queries/audit_planner/audit_roles/use_audit_roles_invalidate";
import { useEngagementsInvalidate } from "PFCore/hooks/queries/engagements";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { TagPayload } from "PFCore/services/activities/tags";
import { Activity } from "PFCore/types";
import { useTranslation } from "react-i18next";

import { ManageTagsModalAction } from "./manage_tags_modal.utils";

type UseManageTagsSubmitProps = {
  activity: Activity | undefined;
  onSuccess?: () => void;
  onError?: () => void;
};

export const useManageTagsSubmit = ({ activity, onSuccess, onError }: UseManageTagsSubmitProps) => {
  const { t } = useTranslation("activities");

  const { showGrowl } = useGrowlStoreActions();
  const growlErrors = useErrorsGrowl();

  const { isRole, isEngagement, isAudit } = useActivityTemplateType(activity);
  const { invalidateRoles } = useRolesInvalidate();
  const { invalidateAll: invalidateEngagements } = useEngagementsInvalidate();
  const { invalidateAll: invalidateAuditRoles } = useAuditRolesInvalidate();
  const { invalidateAuditEngagements } = useAuditEngagementsInvalidate();

  const invalidateActivitiesCache = () => {
    if (isAudit && isRole) {
      invalidateAuditRoles();
    } else if (isAudit && isEngagement) {
      invalidateAuditEngagements();
    } else if (isRole) {
      invalidateRoles();
    } else if (isEngagement) {
      invalidateEngagements();
    }
  };

  const { mutate: createTag } = useCreateTag({
    onSuccess: () => {
      showGrowl({
        message: t("show.tags.message.createSuccess"),
        kind: "success"
      });
      invalidateActivitiesCache();
    },
    onError: (error) => growlErrors(error)
  });
  const { mutate: updateTag } = useUpdateTag({
    onSuccess: () => {
      showGrowl({
        message: t("show.tags.message.updateSuccess"),
        kind: "success"
      });
      invalidateActivitiesCache();
    },
    onError: (error) => growlErrors(error)
  });
  const { mutate: deleteTag } = useDeleteTag({
    onSuccess: () => {
      showGrowl({
        message: t("show.tags.message.deleteSuccess"),
        kind: "success"
      });
      invalidateActivitiesCache();
    },
    onError: ({ response }) => {
      growlErrors(response);
    }
  });

  const handleSubmit = (action: ManageTagsModalAction, payload: TagPayload) => {
    if (!activity) {
      return;
    }

    switch (action) {
      case ManageTagsModalAction.Create:
        return createTag(
          { activityId: activity.id, tagPayload: payload },
          {
            onSuccess,
            onError
          }
        );
      case ManageTagsModalAction.Update:
        return updateTag(
          { activityId: activity.id, tagPayload: payload },
          {
            onSuccess,
            onError
          }
        );
      case ManageTagsModalAction.Delete:
        return deleteTag(
          { activityId: activity.id, notificationRecipientIds: payload.notificationRecipientIds },
          {
            onSuccess,
            onError
          }
        );
    }
  };

  return {
    handleSubmit
  };
};
